import React from 'react';
import { cn } from '../../../utils/cn';
import { formatLocale } from '../../../i18n/format-locale';

/**
 * Defines the possible change types for the delta.
 * - 'positive': Represents a positive change
 * - 'negative': Represents a negative change
 * - 'neutral': Represents no change
 */
type DeltaStatus = 'positive' | 'negative' | 'neutral';

/**
 * Maps change types to their corresponding color classes.
 * This allows for consistent styling based on the change type.
 */

const DELTA_STATUS_STYLES: Record<DeltaStatus, string> = {
  positive: 'text-positive-base border-positive-base bg-positive-background',
  negative: 'text-negative-base border-negative-base bg-negative-background',
  neutral: 'text-neutral-base border-neutral-base bg-neutral-background',
};

/**
 * Common props for both numeric and string deltas.
 */
interface CommonDeltaProps {
  /**
   * Text to display before the delta value.
   * @default ''
   */
  prefix?: string;

  /**
   * Text to display after the delta value.
   * @default ''
   */
  suffix?: string;

  /**
   * Additional CSS classes to apply to the delta component.
   */
  className?: string;

  /**
   * Flag to invert the positive and negative change types. Generally used for findings where a negative delta is a good thing
   * @default false
   */
  isNeutralStyling?: boolean;
}

/**
 * Props for numeric delta values.
 */
export interface NumberDeltaProps extends CommonDeltaProps {
  /**
   * The numeric value of the delta.
   */
  delta: number;

  /**
   * Number of decimal places to display.
   * @default 0
   */
  precision?: number;

  /**
   * Whether to use compact number formatting.
   * @default false
   */
  isCompactNumberFormat?: boolean;

  /**
   * Optional override for change type.
   * If not provided, it will be determined based on the delta value.
   */
  changeType?: DeltaStatus;
}

/**
 * Props for string delta values.
 */
export interface StringDeltaProps extends CommonDeltaProps {
  /**
   * The string value of the delta.
   */
  delta: string;

  /**
   * The change type for string deltas.
   * This is required as it cannot be determined automatically from a string value.
   */
  changeType: DeltaStatus;
}

/**
 * Combined props type for the Delta component.
 * This allows the component to accept either numeric or string delta values,
 * along with the common props.
 */
export type DeltaIndicatorProps = NumberDeltaProps | StringDeltaProps;

const isNumberDeltaProps = (
  props: DeltaIndicatorProps,
): props is NumberDeltaProps => {
  return typeof props.delta === 'number';
};

export const DeltaIndicator: React.FC<DeltaIndicatorProps> = (props) => {
  const {
    changeType,
    prefix = '',
    suffix = '',
    className,
    isNeutralStyling = true,
  } = props;

  let determinedChangeType: DeltaStatus | undefined;
  let formattedValue: string;
  let showSign = false;

  if (isNumberDeltaProps(props)) {
    const { precision = 0, isCompactNumberFormat = false } = props;
    const { value, changeType: calculatedChangeType } = getNumberDeltaProps(
      props.delta,
      precision,
      isCompactNumberFormat,
    );
    formattedValue = value;
    determinedChangeType = changeType || calculatedChangeType;
    showSign = true;
  } else {
    formattedValue = props.delta;
    determinedChangeType = props.changeType;
  }

  const changeTypeClass =
    DELTA_STATUS_STYLES[
      !isNeutralStyling
        ? invertChangeType(determinedChangeType)
        : determinedChangeType || 'neutral'
    ];

  const sign = showSign && determinedChangeType === 'positive' ? '+' : '';

  return (
    <span
      className={cn(
        `inline-flex items-center self-start rounded-md border px-2.5 py-0.5 text-xs font-semibold`,
        changeTypeClass,
        className,
      )}
    >
      {prefix}
      {sign}
      {formattedValue}
      {suffix}
    </span>
  );
};

const getNumberDeltaProps = (
  delta: number,
  precision: number,
  isCompactNumberFormat: boolean,
): { value: string; changeType: DeltaStatus } => {
  const formatter = new Intl.NumberFormat(formatLocale(), {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    notation: 'compact',
    compactDisplay: 'short',
  });

  const value = isCompactNumberFormat
    ? formatter.format(delta)
    : delta.toFixed(precision);
  const changeType: DeltaStatus =
    delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral';

  return { value, changeType };
};

const invertChangeType = (changeType: DeltaStatus): DeltaStatus => {
  switch (changeType) {
    case 'positive':
      return 'negative';
    case 'negative':
      return 'positive';
    default:
      return 'neutral';
  }
};
